import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-setup-account',
  templateUrl: './setup-account.component.html',
  styleUrls: ['./setup-account.component.scss']
})
export class SetupAccountComponent implements OnInit {
  setupForm: FormGroup;
  token: string = '';
  isSubmitting: boolean = false;
  isValidatingToken: boolean = true;
  tokenValid: boolean = false;
  tokenExpired: boolean = false;
  tokenError: string = '';
  userEmail: string = '';
  userName: string = '';
  userType: string = '';
  hideTemporaryPassword: boolean = true;
  hidePassword: boolean = true;
  hideConfirmPassword: boolean = true;
  setupComplete: boolean = false;

  // Password policy regex - matches login and reset password requirements (8-16 characters)
  passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-+_!@#$%^&*., ?])[A-Za-z\d-+_!@#$%^&*., ?]{8,16}$/;

  // Password requirements
  passwordRequirements = {
    minLength: false,
    maxLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private utility: UtilityService
  ) {
    this.setupForm = this.fb.group({
      temporaryPassword: ['', [Validators.required]],
      password: ['', [
        Validators.required,
        Validators.pattern(this.passwordRegex),
        this.noWhitespaceValidator
      ]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Get token from query params
    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
      if (this.token) {
        this.validateToken();
      } else {
        this.isValidatingToken = false;
        this.tokenError = 'No setup token provided. Please check your email for the setup link.';
      }
    });

    // Listen to password changes for real-time validation
    this.setupForm.get('password')?.valueChanges.subscribe(password => {
      this.checkPasswordRequirements(password);
    });
  }

  /**
   * Validate the provisioning token
   */
  validateToken(): void {
    this.isValidatingToken = true;

    this.api.apiRequest('get', `user/validateSetupToken?token=${this.token}`, {}).subscribe(
      (res) => {
        this.isValidatingToken = false;
        if (res.status === 'success') {
          const setupData = res.data?.data || res.data || {};
          this.tokenValid = true;
          this.userEmail = setupData.email || '';
          this.userName = `${setupData.firstName || ''} ${setupData.lastName || ''}`.trim();
          this.userType = setupData.userType || '';
          if (this.userType === 'admin') {
            this.setupForm.get('temporaryPassword')?.clearValidators();
            this.setupForm.get('temporaryPassword')?.updateValueAndValidity();
          }
        } else {
          this.tokenValid = false;
          this.tokenError = res.message || 'Invalid or expired setup link.';
        }
      },
      (err) => {
        this.isValidatingToken = false;
        this.tokenValid = false;

        if (err.error?.errorCode === 'TOKEN_EXPIRED') {
          this.tokenExpired = true;
          this.tokenError = 'This setup link has expired. Please contact your administrator to resend the invitation.';
        } else {
          this.tokenError = err.error?.message || 'Invalid setup link. Please contact your administrator.';
        }
      }
    );
  }

  /**
   * Check password requirements
   */
  checkPasswordRequirements(password: string): void {
    this.passwordRequirements = {
      minLength: password.length >= 8,
      maxLength: password.length <= 16,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[-+_!@#$%^&*., ?]/.test(password)
    };
  }

  /**
   * Validator to check for whitespace in password
   */
  noWhitespaceValidator(control: any) {
    const isWhitespace = (control.value || '').match(/\s/g);
    const isValid = !isWhitespace;
    return isValid ? null : { 'whitespace': true };
  }

  /**
   * Custom validator to check if passwords match
   */
  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }

  /**
   * Check if all password requirements are met
   */
  isPasswordValid(): boolean {
    return Object.values(this.passwordRequirements).every(req => req === true);
  }

  /**
   * Submit setup form
   */
  onSubmit(): void {
    if (this.setupForm.invalid || !this.isPasswordValid()) {
      this.utility.openSnackBar('Please ensure all requirements are met.', 'Ok');
      return;
    }

    this.isSubmitting = true;
    const temporaryPassword = this.setupForm.get('temporaryPassword')?.value;
    const newPassword = this.setupForm.get('password')?.value;

    const reqParams: any = {
      token: this.token,
      newPassword: newPassword
    };
    if (this.userType !== 'admin') {
      reqParams.temporaryPassword = temporaryPassword;
    }

    this.api.apiRequest('post', 'user/completeSetup', reqParams).subscribe(
      (res) => {
        this.isSubmitting = false;
        if (res.status === 'success') {
          this.setupComplete = true;

          // Redirect to appropriate sign-in page after 3 seconds
          setTimeout(() => {
            const userType = res.data?.data?.userType || res.data?.userType || 'specialist';
            if (userType === 'admin') {
              this.router.navigate(['/auth/admin-signin']);
            } else if (userType === 'pcv' || userType === 'vc') {
              this.router.navigate(['/auth/pcv-signin']);
            } else {
              this.router.navigate(['/auth/specialist-signin']);
            }
          }, 3000);
        } else {
          this.utility.openSnackBar(res.message || 'Failed to complete setup.', 'Ok');
        }
      },
      (err) => {
        this.isSubmitting = false;
        const errorMessage = err.error?.message || 'An error occurred while completing setup.';
        this.utility.openSnackBar(errorMessage, 'Ok');
        console.error('Setup error:', err);
      }
    );
  }

  /**
   * Get form control for template access
   */
  get f() {
    return this.setupForm.controls;
  }

  /**
   * Request new invitation link
   */
  requestNewLink(): void {
    this.utility.openSnackBar('Please contact your administrator to resend the invitation.', 'Ok');
  }
}
